import { Link } from "@tanstack/react-router";
import {
  AudioLines,
  ChevronRight,
  MessageSquareText,
  MoreHorizontal,
  Pencil,
  SquarePen,
  Trash2,
} from "lucide-react";
import type { JSX } from "react";
import {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { BackToVisits } from "@/components/layout/v3/V3Shell";
import { WithDictation } from "@/components/ui/DictationButton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { V3IconButton } from "@/components/ui/v3/button";
import { V3ConfirmSheet } from "@/components/ui/v3/confirm-sheet";
import {
  V3_MENU_CONTENT,
  V3_MENU_ITEM,
  V3_MENU_ITEM_DESTRUCTIVE,
  V3_MENU_SEPARATOR,
} from "@/components/ui/v3/menu";
import { cn } from "@/lib/utils";
import { useFeedbackStore } from "@/store/feedback.store";
import { usePhoneLayout } from "../layout";
import { VerifyGateProvider } from "../VerifyReview";
import { useDocumentControls } from "./DocumentControls";

interface TopBarProps {
  visitPublicId: string | null;
  title: string;
  // The note's actions (NoteActions) once its meta line has scrolled away.
  // 640–1023px then also show the title (no breadcrumb there; phones have
  // no room for it), and phones get Export in "…".
  noteActions?: React.ReactNode;
  // Before the note's actions (a pre-chart draft's Start visit).
  primaryAction?: React.ReactNode;
  onOpenTranscript: () => void;
  onRename: () => void;
  onDelete: () => void;
  deleting?: boolean;
  disabled?: boolean;
}

// The visit's top bar in the v3.2 panel (iPad/iPhone): back (phones) or the
// sidebar toggle, the breadcrumb on desktop, then "…" (Edit note,
// Transcript, version history, Rename, Send feedback, Delete). Export,
// Create and Copy to chart sit on the note's meta line (NoteMetaBar) and
// join this row, before "…", once that line scrolls away.
export const VisitTopBar = ({
  visitPublicId,
  title,
  noteActions,
  primaryAction,
  onOpenTranscript,
  onRename,
  onDelete,
  deleting = false,
  disabled = false,
}: TopBarProps): JSX.Element => {
  const { t } = useTranslation();
  const controls = useDocumentControls();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const phone = usePhoneLayout();
  const exportInMenu =
    !!noteActions && phone && isValidElement(controls?.exportAction);
  return (
    <header
      className="@container flex h-14 shrink-0 items-center gap-1 border-b border-transparent px-2 font-v3 text-v3-text sm:px-3 lg:h-[52px] lg:px-4"
      data-testid="visit-top-bar"
    >
      <BackToVisits />
      {noteActions && (
        <span
          className="min-w-0 flex-1 truncate text-[15px] font-semibold animate-in fade-in duration-150 motion-reduce:animate-none max-sm:hidden lg:hidden"
          data-testid="top-bar-title"
          title={title}
        >
          {title}
        </span>
      )}
      <nav
        aria-label={t("v3.shell.breadcrumb")}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-[13px] text-v3-text-3 max-lg:hidden",
          // The note's actions joined the bar: the breadcrumb steps aside
          // where they leave it no room, instead of sitting under them.
          noteActions && "@max-[52rem]:hidden",
        )}
        data-testid="visit-breadcrumb"
      >
        <Link
          className="shrink-0 rounded-md px-1 py-0.5 hover:bg-v3-surface-2 hover:text-v3-text"
          to="/visits"
        >
          {t("visits.title")}
        </Link>
        <ChevronRight aria-hidden className="size-3.5 shrink-0" />
        <span className="truncate font-medium text-v3-text" title={title}>
          {title}
        </span>
      </nav>
      <div className="ml-auto flex shrink-0 items-center gap-1">
        {primaryAction}
        {noteActions}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <V3IconButton
              data-testid="visit-more-menu"
              disabled={disabled}
              label={t("visit.workspace.moreNoteActions")}
            >
              <MoreHorizontal />
            </V3IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className={cn(
              V3_MENU_CONTENT,
              "max-h-[70dvh] w-64 overflow-y-auto",
            )}
          >
            <DropdownMenuItem
              className={V3_MENU_ITEM}
              data-testid="menu-edit-note"
              disabled={!controls?.canEdit}
              onSelect={() => controls?.startEditing()}
            >
              <SquarePen aria-hidden />
              {t("v3.workspace.editNote")}
            </DropdownMenuItem>
            <DropdownMenuItem
              className={V3_MENU_ITEM}
              data-testid="menu-transcript"
              onSelect={onOpenTranscript}
            >
              <AudioLines aria-hidden />
              {t("visit.workspace.transcript")}
            </DropdownMenuItem>
            {exportInMenu && isValidElement(controls?.exportAction) && (
              <>
                <DropdownMenuSeparator className={V3_MENU_SEPARATOR} />
                <VerifyGateProvider value={controls.gate}>
                  {cloneElement(
                    controls.exportAction as React.ReactElement<{
                      asMenuItems?: boolean;
                    }>,
                    { asMenuItems: true },
                  )}
                </VerifyGateProvider>
              </>
            )}
            {controls?.menuItems && (
              <>
                <DropdownMenuSeparator className={V3_MENU_SEPARATOR} />
                <div className="[&_[role^=menuitem]]:min-h-11 lg:[&_[role^=menuitem]]:min-h-9 [&_[role^=menuitem]]:rounded-lg [&_[role^=menuitem]]:text-[14px]">
                  {controls.menuItems}
                </div>
              </>
            )}
            <DropdownMenuSeparator className={V3_MENU_SEPARATOR} />
            <DropdownMenuItem
              className={V3_MENU_ITEM}
              data-testid="menu-rename"
              onSelect={onRename}
            >
              <Pencil aria-hidden />
              {t("v3.workspace.rename")}
            </DropdownMenuItem>
            <DropdownMenuItem
              className={V3_MENU_ITEM}
              onSelect={() =>
                useFeedbackStore.getState().show({ visitPublicId })
              }
            >
              <MessageSquareText aria-hidden />
              {t("visit.workspace.feedback")}
            </DropdownMenuItem>
            <DropdownMenuSeparator className={V3_MENU_SEPARATOR} />
            <DropdownMenuItem
              className={cn(V3_MENU_ITEM, V3_MENU_ITEM_DESTRUCTIVE)}
              data-testid="menu-delete"
              disabled={deleting}
              onSelect={() => setConfirmDelete(true)}
            >
              <Trash2 aria-hidden />
              {t("v3.workspace.deleteVisit")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <V3ConfirmSheet
        busy={deleting}
        confirmLabel={t("v3.workspace.deleteVisit")}
        description={t("v3.workspace.deleteDescription")}
        destructive
        open={confirmDelete}
        testId="delete-visit-confirm"
        title={t("v3.workspace.deleteTitle", { title })}
        onConfirm={onDelete}
        onOpenChange={setConfirmDelete}
      />
    </header>
  );
};

interface TitleProps {
  title: string;
  renaming: boolean;
  onRenamingChange: (renaming: boolean) => void;
  // Resolves true once saved.
  onRename: (title: string) => Promise<boolean>;
  disabled?: boolean;
}

// The title, renamed in place: click it (or "Rename" in "…"), Enter saves,
// Escape cancels. The meta line above it is NoteMetaBar.
export const VisitTitle = ({
  title,
  renaming,
  onRenamingChange,
  onRename,
  disabled = false,
}: TitleProps): JSX.Element => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (renaming) {
      setDraft(title);
      requestAnimationFrame(() => {
        input.current?.focus();
        input.current?.select();
      });
    }
  }, [renaming, title]);

  const save = async (): Promise<void> => {
    const next = draft.trim();
    if (!next || next === title) {
      onRenamingChange(false);
      return;
    }
    setSaving(true);
    const saved = await Promise.resolve(onRename(next)).catch(() => false);
    setSaving(false);
    if (saved) onRenamingChange(false);
  };

  const titleClass =
    "font-v3-display text-[28px] font-bold leading-[1.15] tracking-[-0.02em] text-v3-text sm:text-[32px]";
  return (
    <div className="mb-5 font-v3">
      {renaming ? (
        // The same mic as every text field (VITE_DICTATION_EVERYWHERE_ENABLED);
        // it keeps focus in the field, so blur still means "done".
        <WithDictation fieldLabel={t("dictation.field.fields.visitTitle")}>
          <input
            ref={input}
            aria-label={t("visit.updateTitle")}
            className={cn(
              titleClass,
              "-mx-2 w-[calc(100%+16px)] rounded-[10px] bg-v3-surface-2 px-2 outline-hidden ring-2 ring-v3-brand-ring",
            )}
            data-testid="visit-title-input"
            disabled={saving}
            maxLength={200}
            value={draft}
            onBlur={() => void save()}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void save();
              } else if (event.key === "Escape") {
                event.preventDefault();
                onRenamingChange(false);
              }
            }}
          />
        </WithDictation>
      ) : (
        <h1 className={cn(titleClass, "text-balance wrap-break-word")}>
          <button
            className="-mx-1 rounded-lg px-1 text-left hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-v3-brand disabled:hover:bg-transparent"
            data-testid="visit-title"
            disabled={disabled}
            title={t("visit.updateTitle")}
            type="button"
            onClick={() => onRenamingChange(true)}
          >
            {title}
          </button>
        </h1>
      )}
    </div>
  );
};
